<template>
  <div class="push-msg">
    <APageHead title="푸시 알림 관리" sub="FCM 기반 실시간 Push 알림 전송 및 관리 시스템" />

    <ACard body-flush>
      <div class="p-tabs">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          type="button"
          class="p-tabs__item"
          :class="{ 'is-active': activeTab === tab.id }"
          @click="activeTab = tab.id"
        >
          <i class="material-icons-round">{{ tab.icon }}</i>
          {{ tab.label }}
        </button>
      </div>

      <div class="push-msg__pane">
        <!-- 전역 공지 -->
        <div v-if="activeTab === 'global'" class="push-msg__grid">
          <div class="push-msg__main">
            <h5 class="push-msg__sub-h">
              <i class="material-icons-round">campaign</i>
              전역 공지 전송
            </h5>
            <p class="push-msg__sub-d">모든 사용자에게 알림을 전송합니다</p>

            <div class="p-field">
              <label class="p-field__label">제목 <span class="p-field__req">*</span></label>
              <input v-model="globalForm.title" class="p-input" placeholder="알림 제목을 입력하세요" maxlength="100" />
              <p class="p-field__hint">{{ globalForm.title.length }}/100자</p>
            </div>

            <div class="p-field">
              <label class="p-field__label">내용 <span class="p-field__req">*</span></label>
              <textarea v-model="globalForm.body" class="p-input p-input--area" rows="6" placeholder="알림 내용을 입력하세요" maxlength="500" />
              <p class="p-field__hint">{{ globalForm.body.length }}/500자</p>
            </div>

            <div class="push-msg__alert push-msg__alert--info">
              <i class="material-icons-round">info</i>
              <div>
                <strong>전송 안내</strong>
                <ul>
                  <li>디바이스 토큰이 등록된 모든 사용자에게 전송됩니다</li>
                  <li>전송 후 취소할 수 없으니 신중하게 작성해주세요</li>
                </ul>
              </div>
            </div>

            <div class="push-msg__actions">
              <ABtn
                kind="yellow"
                icon="send"
                block
                :disabled="!globalForm.title || !globalForm.body || sending"
                :loading="sending"
                @click="sendGlobalNotification"
              >{{ sending ? '전송 중' : '전역 공지 전송' }}</ABtn>
              <ABtn kind="ghost" icon="refresh" @click="resetGlobalForm">초기화</ABtn>
            </div>
          </div>
          <div class="push-msg__side">
            <ACard>
              <h6 class="push-msg__side-h">📌 전송 가이드</h6>
              <ul class="push-msg__guide">
                <li><APill kind="yellow" dot>전체 발송</APill><span>모든 앱 사용자 대상</span></li>
                <li><APill kind="ok" dot>자동 구독</APill><span>토큰 등록 시 자동</span></li>
                <li><APill kind="warn" dot>관리자 전용</APill><span>emp 권한 필요</span></li>
              </ul>
            </ACard>
          </div>
        </div>

        <!-- 클래스별 공지 -->
        <div v-else-if="activeTab === 'class'" class="push-msg__grid">
          <div class="push-msg__main">
            <h5 class="push-msg__sub-h">
              <i class="material-icons-round">groups</i>
              클래스별 공지 전송
            </h5>
            <p class="push-msg__sub-d">특정 클래스 수강생에게만 전송</p>

            <div class="push-msg__search-block">
              <label class="p-field__label">클래스 선택 <span class="p-field__req">*</span></label>
              <div class="p-form-grid p-form-grid--3">
                <div class="p-field">
                  <label class="p-field__label">클래스명</label>
                  <input v-model="classSearch.class_name" class="p-input" placeholder="클래스명으로 검색" />
                </div>
                <div class="p-field">
                  <label class="p-field__label">강사명</label>
                  <input v-model="classSearch.instructor_name" class="p-input" placeholder="강사명으로 검색" />
                </div>
                <div class="push-msg__search-action">
                  <ABtn
                    kind="yellow"
                    icon="search"
                    block
                    :disabled="!(classSearch.class_name || classSearch.instructor_name)"
                    @click="searchClasses"
                  >검색</ABtn>
                </div>
              </div>

              <ATable
                v-if="classSearch.results.length"
                :columns="classroomCols"
                :rows="classSearch.results"
                dense
              >
                <template #cell:status="{ row }">
                  <APill kind="default">{{ row.class_status || row.status || '-' }}</APill>
                </template>
                <template #cell:actions="{ row }">
                  <APill v-if="isClassroomSelected(row)" kind="ok">선택됨</APill>
                  <ABtn v-else kind="ghost" size="sm" @click="selectClassroom(row)">선택</ABtn>
                </template>
              </ATable>
              <p v-else-if="classSearch.class_name || classSearch.instructor_name" class="push-msg__no-result">검색 결과가 없습니다.</p>
            </div>

            <div v-if="selectedClassroom" class="push-msg__alert push-msg__alert--ok">
              <i class="material-icons-round">check_circle</i>
              <span><strong>{{ selectedClassroom.name }}</strong> 선택됨</span>
            </div>

            <div class="p-field">
              <label class="p-field__label">제목 <span class="p-field__req">*</span></label>
              <input v-model="classForm.title" class="p-input" placeholder="알림 제목" maxlength="100" />
              <p class="p-field__hint">{{ classForm.title.length }}/100자</p>
            </div>

            <div class="p-field">
              <label class="p-field__label">내용 <span class="p-field__req">*</span></label>
              <textarea v-model="classForm.body" class="p-input p-input--area" rows="6" placeholder="알림 내용" maxlength="500" />
              <p class="p-field__hint">{{ classForm.body.length }}/500자</p>
            </div>

            <div class="push-msg__actions">
              <ABtn
                kind="yellow"
                icon="send"
                block
                :disabled="!classForm.classroom_idx || !classForm.title || !classForm.body || sending"
                :loading="sending"
                @click="sendClassNotification"
              >{{ sending ? '전송 중' : '클래스 공지 전송' }}</ABtn>
              <ABtn kind="ghost" icon="refresh" @click="resetClassForm">초기화</ABtn>
            </div>
          </div>
          <div class="push-msg__side">
            <ACard>
              <h6 class="push-msg__side-h">📌 전송 가이드</h6>
              <ul class="push-msg__guide">
                <li><APill kind="ok" dot>타겟팅 발송</APill><span>특정 클래스만 대상</span></li>
                <li><APill kind="info" dot>토픽 구독</APill><span>class_{classroom_idx}</span></li>
                <li><APill kind="warn" dot>활용 사례</APill><span>과제, 수업 공지</span></li>
              </ul>
            </ACard>
          </div>
        </div>

        <!-- 개인 알림 -->
        <div v-else-if="activeTab === 'personal'" class="push-msg__grid">
          <div class="push-msg__main">
            <h5 class="push-msg__sub-h">
              <i class="material-icons-round">person</i>
              개인 알림 전송
            </h5>
            <p class="push-msg__sub-d">특정 사용자에게 1:1 알림</p>

            <div class="push-msg__search-block">
              <label class="p-field__label">사용자 선택 <span class="p-field__req">*</span></label>
              <div class="p-form-grid p-form-grid--3">
                <div class="p-field">
                  <label class="p-field__label">이름</label>
                  <input v-model="userSearch.name" class="p-input" placeholder="이름으로 검색" />
                </div>
                <div class="p-field">
                  <label class="p-field__label">이메일</label>
                  <input v-model="userSearch.email" class="p-input" placeholder="이메일로 검색" />
                </div>
                <div class="push-msg__search-action">
                  <ABtn
                    kind="yellow"
                    icon="search"
                    block
                    :disabled="!(userSearch.name || userSearch.email)"
                    @click="searchUsers"
                  >검색</ABtn>
                </div>
              </div>

              <ATable
                v-if="userSearch.results.length"
                :columns="userCols"
                :rows="userSearch.results"
                dense
              >
                <template #cell:actions="{ row }">
                  <APill v-if="isUserSelected(row)" kind="warn">선택됨</APill>
                  <ABtn v-else kind="ghost" size="sm" @click="selectUser(row)">선택</ABtn>
                </template>
              </ATable>
              <p v-else-if="userSearch.name || userSearch.email" class="push-msg__no-result">검색 결과가 없습니다.</p>
            </div>

            <div v-if="selectedUser" class="push-msg__alert push-msg__alert--warn">
              <i class="material-icons-round">person_check</i>
              <span><strong>{{ selectedUser.name }}</strong> ({{ selectedUser.email }})</span>
            </div>

            <div class="p-field">
              <label class="p-field__label">제목 <span class="p-field__req">*</span></label>
              <input v-model="personalForm.title" class="p-input" placeholder="알림 제목" maxlength="100" />
              <p class="p-field__hint">{{ personalForm.title.length }}/100자</p>
            </div>

            <div class="p-field">
              <label class="p-field__label">내용 <span class="p-field__req">*</span></label>
              <textarea v-model="personalForm.body" class="p-input p-input--area" rows="5" placeholder="알림 내용" maxlength="500" />
              <p class="p-field__hint">{{ personalForm.body.length }}/500자</p>
            </div>

            <div class="p-field">
              <label class="p-field__label">추가 데이터 (JSON, 선택)</label>
              <textarea
                v-model="personalForm.dataJson"
                class="p-input p-input--area push-msg__json"
                rows="4"
                placeholder='{"type": "reminder", "link": "pineapple://course/101"}'
              />
              <p v-if="personalDataError" class="p-field__error">{{ personalDataError }}</p>
            </div>

            <div class="push-msg__actions">
              <ABtn
                kind="yellow"
                icon="send"
                block
                :disabled="!personalForm.usersIdx || !personalForm.title || !personalForm.body || sending"
                :loading="sending"
                @click="sendPersonalNotification"
              >{{ sending ? '전송 중' : '개인 알림 전송' }}</ABtn>
              <ABtn kind="ghost" icon="refresh" @click="resetPersonalForm">초기화</ABtn>
            </div>
          </div>
          <div class="push-msg__side">
            <ACard>
              <h6 class="push-msg__side-h">📌 전송 가이드</h6>
              <ul class="push-msg__guide">
                <li><APill kind="warn" dot>1:1 전송</APill><span>특정 사용자 1명 대상</span></li>
                <li><APill kind="info" dot>딥링크</APill><span>앱 내 화면 이동</span></li>
                <li><APill kind="ok" dot>리마인더</APill><span>개인 메시지 활용</span></li>
              </ul>
            </ACard>

            <ACard>
              <h6 class="push-msg__side-h">추가 데이터 예시</h6>
              <pre class="push-msg__code">{{ jsonExample }}</pre>
            </ACard>
          </div>
        </div>

        <!-- 전송 이력 -->
        <div v-else-if="activeTab === 'history'">
          <div class="push-msg__history-stats">
            <ACard>
              <AStat label="전체 발송" :value="String(historyStats.total)" />
            </ACard>
            <ACard>
              <AStat label="성공" :value="String(historyStats.success)" />
            </ACard>
            <ACard>
              <AStat label="실패" :value="String(historyStats.failed)" />
            </ACard>
            <ACard>
              <AStat label="성공률" :value="String(historyStats.successRate)" unit="%" />
            </ACard>
          </div>

          <div class="push-msg__filter-card">
            <div class="push-msg__filters">
              <div class="p-field push-msg__filter push-msg__filter--sm">
                <label class="p-field__label">알림 유형</label>
                <select v-model="historyFilter.notification_type" class="p-input">
                  <option value="">전체</option>
                  <option value="global">전역 공지</option>
                  <option value="class">클래스 공지</option>
                  <option value="personal">개인 알림</option>
                </select>
              </div>
              <div class="p-field push-msg__filter push-msg__filter--sm">
                <label class="p-field__label">전송 결과</label>
                <select v-model="historyFilter.success" class="p-input">
                  <option value="">전체</option>
                  <option value="true">성공</option>
                  <option value="false">실패</option>
                </select>
              </div>
              <div class="p-field push-msg__filter push-msg__filter--md">
                <label class="p-field__label">시작 일시</label>
                <input v-model="historyFilter.sent_from" type="datetime-local" class="p-input" />
              </div>
              <div class="p-field push-msg__filter push-msg__filter--md">
                <label class="p-field__label">종료 일시</label>
                <input v-model="historyFilter.sent_to" type="datetime-local" class="p-input" />
              </div>
              <div class="push-msg__filter-btns">
                <ABtn kind="ghost" icon="refresh" @click="resetHistoryFilter">초기화</ABtn>
                <ABtn kind="yellow" icon="search" @click="loadHistory(1)">조회</ABtn>
              </div>
            </div>
          </div>

          <AEmpty v-if="!historyLoading && historyList.length === 0" title="전송 이력이 없습니다" />

          <ATable
            v-else
            :columns="historyCols"
            :rows="pagedHistoryList"
            row-key="idx"
          >
            <template #cell:sent_at="{ row }">
              <span class="push-msg__num">{{ formatDateTime(row.sent_at) }}</span>
            </template>
            <template #cell:notification_type="{ row }">
              <APill :kind="typePillKind(row.notification_type)">{{ getTypeLabel(row.notification_type) }}</APill>
            </template>
            <template #cell:title="{ row }">
              <strong>{{ row.title }}</strong>
            </template>
            <template #cell:target="{ row }">
              <span v-if="row.target_topic">{{ row.target_topic }}</span>
              <span v-else-if="row.target_user_idx">(User) {{ row.real_target_name }}</span>
              <span v-else>-</span>
            </template>
            <template #cell:sender="{ row }">
              ({{ row.sender_type }}) {{ row.real_sender_name }}
            </template>
            <template #cell:success="{ row }">
              <APill :kind="row.success ? 'ok' : 'err'">{{ row.success ? '성공' : '실패' }}</APill>
            </template>
            <template #cell:actions="{ row }">
              <ABtn kind="ghost" size="sm" icon="visibility" icon-only @click="showHistoryDetail(row)" />
            </template>
          </ATable>

          <div v-if="historyList.length > 0" class="push-msg__pager-row">
            <div class="push-msg__pager-total">
              총 <strong>{{ historyList.length.toLocaleString() }}</strong>건 · {{ historyPage }} / {{ historyTotalPages }} 페이지
              <select v-model.number="historyPageSize" class="p-input push-msg__pager-size" @change="historyPage = 1">
                <option :value="10">10개씩</option>
                <option :value="20">20개씩</option>
                <option :value="50">50개씩</option>
                <option :value="100">100개씩</option>
              </select>
            </div>
            <div v-if="historyTotalPages > 1" class="push-msg__pager">
              <ABtn
                kind="ghost"
                size="sm"
                icon="chevron_left"
                icon-only
                :disabled="historyPage === 1"
                @click="historyPage = historyPage - 1"
              />
              <button
                v-for="page in historyDisplayPages"
                :key="page"
                type="button"
                class="push-msg__pager-num"
                :class="{ 'is-active': page === historyPage }"
                @click="historyPage = page"
              >{{ page }}</button>
              <ABtn
                kind="ghost"
                size="sm"
                icon="chevron_right"
                icon-only
                :disabled="historyPage === historyTotalPages"
                @click="historyPage = historyPage + 1"
              />
            </div>
          </div>
        </div>

        <!-- 관리 기능 -->
        <div v-else-if="activeTab === 'admin'">
          <ACard>
            <h5 class="push-msg__sub-h">
              <i class="material-icons-round">cleaning_services</i>
              중복 토큰 정리
            </h5>
            <p class="push-msg__sub-d">데이터 무결성 유지를 위한 관리 기능</p>

            <p class="push-msg__admin-d">
              동일 사용자에게 여러 개의 디바이스 토큰이 등록된 경우, 가장 최근 토큰만 남기고 삭제합니다.
            </p>

            <div v-if="duplicateStats" class="push-msg__alert push-msg__alert--warn">
              <i class="material-icons-round">warning</i>
              <div>
                <strong>현재 중복 상태</strong>
                <p>중복된 사용자: <strong>{{ duplicateStats.duplicate_count }}명</strong></p>
              </div>
            </div>

            <div class="push-msg__admin-actions">
              <ABtn kind="yellow" icon="bar_chart" block :disabled="adminLoading" @click="getDuplicateStats">중복 통계 조회</ABtn>
              <ABtn kind="danger-fill" icon="delete" block :disabled="adminLoading" @click="cleanupDuplicates">중복 정리 실행</ABtn>
            </div>
          </ACard>

          <ACard class="push-msg__guide-card">
            <h5 class="push-msg__sub-h">
              <i class="material-icons-round">info</i>
              관리 가이드라인
            </h5>
            <div class="push-msg__guide-grid">
              <div class="push-msg__guide-item">
                <div class="push-msg__guide-icon"><i class="material-icons-round">event</i></div>
                <h6>정기 점검</h6>
                <p>월 1회 중복 토큰 정리 권장</p>
              </div>
              <div class="push-msg__guide-item">
                <div class="push-msg__guide-icon push-msg__guide-icon--ok"><i class="material-icons-round">monitoring</i></div>
                <h6>활동성 모니터링</h6>
                <p>토큰 통계로 사용자 추이 파악</p>
              </div>
              <div class="push-msg__guide-item">
                <div class="push-msg__guide-icon push-msg__guide-icon--warn"><i class="material-icons-round">schedule</i></div>
                <h6>자동 정리</h6>
                <p>90일 이상 만료 토큰 자동 삭제</p>
              </div>
              <div class="push-msg__guide-item">
                <div class="push-msg__guide-icon push-msg__guide-icon--info"><i class="material-icons-round">description</i></div>
                <h6>로그 기록</h6>
                <p>모든 관리 작업 자동 기록</p>
              </div>
            </div>
          </ACard>
        </div>
      </div>
    </ACard>

    <!-- 이력 상세 모달 -->
    <AModal v-model="historyDetailOpen" title="알림 전송 상세" size="md">
      <div v-if="selectedHistory" class="push-msg__detail">
        <div class="push-msg__detail-row">
          <span class="push-msg__detail-label">전송시간</span>
          <span class="push-msg__detail-val">{{ formatDateTime(selectedHistory.sent_at) }}</span>
        </div>
        <div class="push-msg__detail-row">
          <span class="push-msg__detail-label">알림 유형</span>
          <APill :kind="typePillKind(selectedHistory.notification_type)">{{ getTypeLabel(selectedHistory.notification_type) }}</APill>
        </div>
        <div class="push-msg__detail-row">
          <span class="push-msg__detail-label">제목</span>
          <span class="push-msg__detail-val">{{ selectedHistory.title }}</span>
        </div>
        <div class="push-msg__detail-row">
          <span class="push-msg__detail-label">내용</span>
          <span class="push-msg__detail-val">{{ selectedHistory.body }}</span>
        </div>
        <div class="push-msg__detail-row">
          <span class="push-msg__detail-label">전송 결과</span>
          <APill :kind="selectedHistory.success ? 'ok' : 'err'">{{ selectedHistory.success ? '성공' : '실패' }}</APill>
        </div>
        <div v-if="selectedHistory.error_message" class="push-msg__alert push-msg__alert--err">
          <i class="material-icons-round">error</i>
          <span><strong>오류 메시지:</strong> {{ selectedHistory.error_message }}</span>
        </div>
      </div>
      <template #foot>
        <ABtn kind="ghost" @click="historyDetailOpen = false">닫기</ABtn>
      </template>
    </AModal>
  </div>
</template>

<script>
import APageHead from "@/components/Pineapple/APageHead.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import APill from "@/components/Pineapple/APill.vue";
import AStat from "@/components/Pineapple/AStat.vue";
import ATable from "@/components/Pineapple/ATable.vue";
import AModal from "@/components/Pineapple/AModal.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";
// this.$swal 은 common.js 래퍼라 인자를 하나만 받습니다. (제목, 내용, 아이콘) 으로 넘기면 내용·아이콘이 버려지고
// 제목만 2초 떴다 사라지므로, 그 형태의 알림창은 sweetalert2 를 직접 씁니다.
import swal from "sweetalert2";
import { apiErrorMessage } from "@/utils/apiError";

export default {
  name: 'SystemPushMassages',
  components: { APageHead, ACard, ABtn, APill, AStat, ATable, AModal, AEmpty },
  data() {
    return {
      activeTab: 'global',
      sending: false,
      tabs: [
        { id: 'global',   label: '전역 공지',     icon: 'campaign' },
        { id: 'class',    label: '클래스별 공지', icon: 'groups' },
        { id: 'personal', label: '개인 알림',     icon: 'person' },
        { id: 'history',  label: '전송 이력',     icon: 'history' },
        { id: 'admin',    label: '관리 기능',     icon: 'settings' },
      ],
      globalForm: { title: '', body: '' },
      classForm: { classroom_idx: null, title: '', body: '' },
      selectedClassroom: null,
      classSearch: { class_name: '', instructor_name: '', results: [] },
      personalForm: { usersIdx: null, title: '', body: '', dataJson: '' },
      selectedUser: null,
      personalDataError: '',
      userSearch: { name: '', email: '', results: [] },
      historyList: [],
      historyLoading: false,
      historyPage: 1,
      historyPageSize: 20,
      historyFilter: { notification_type: '', success: '', sent_from: '', sent_to: '' },
      selectedHistory: null,
      historyDetailOpen: false,
      adminLoading: false,
      duplicateStats: null,
      classroomCols: [
        { key: 'id', label: 'ID', width: 70 },
        { key: 'name', label: '클래스명' },
        { key: 'instructor_name', label: '강사' },
        { key: 'status', label: '상태', width: 100 },
        { key: 'actions', label: '선택', width: 90, align: 'center' },
      ],
      userCols: [
        { key: 'id', label: 'ID', width: 70 },
        { key: 'name', label: '이름' },
        { key: 'email', label: '이메일' },
        { key: 'actions', label: '선택', width: 90, align: 'center' },
      ],
      historyCols: [
        { key: 'sent_at',           label: '전송시간', width: 150 },
        { key: 'notification_type', label: '유형',     width: 90 },
        { key: 'title',             label: '제목' },
        { key: 'target',            label: '대상' },
        { key: 'sender',            label: '발송자' },
        { key: 'success',           label: '결과', width: 80, align: 'center' },
        { key: 'actions',           label: '', width: 60, align: 'center' },
      ],
      jsonExample: JSON.stringify({
        type: 'reminder',
        course_id: '101',
        deep_link: 'pineapple://course/101',
      }, null, 2),
    };
  },
  computed: {
    historyStats() {
      const total = this.historyList.length;
      const success = this.historyList.filter((item) => item.success).length;
      const failed = total - success;
      const successRate = total > 0 ? Math.round((success / total) * 100) : 0;
      return { total, success, failed, successRate };
    },
    historyTotalPages() {
      return Math.max(1, Math.ceil(this.historyList.length / this.historyPageSize));
    },
    pagedHistoryList() {
      const start = (this.historyPage - 1) * this.historyPageSize;
      return this.historyList.slice(start, start + this.historyPageSize);
    },
    historyDisplayPages() {
      const pages = [];
      const maxDisplay = 5;
      const half = Math.floor(maxDisplay / 2);
      let start = Math.max(1, this.historyPage - half);
      let end = Math.min(this.historyTotalPages, start + maxDisplay - 1);
      if (end - start < maxDisplay - 1) start = Math.max(1, end - maxDisplay + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    },
  },
  mounted() {
    this.loadHistory();
  },
  methods: {
    async sendGlobalNotification() {
      if (!this.globalForm.title || !this.globalForm.body) {
        swal.fire('알림', '제목과 내용을 모두 입력해주세요.', 'warning');
        return;
      }
      const confirm = await this.$swal({
        title: '전역 공지 전송',
        text: '모든 사용자에게 알림을 전송하시겠습니까?',
        icon: 'question',
        showCancelButton: true, showConfirmButton: true,
        confirmButtonText: '전송', cancelButtonText: '취소', reverseButtons: true,
      });
      if (!confirm.isConfirmed) return;

      this.sending = true;
      try {
        const response = await this.$axios({
          method: 'post',
          url: '/notification/send-global-notification/',
          data: { title: this.globalForm.title, body: this.globalForm.body },
          loadingBar: true,
        });
        if (response.data.success) {
          swal.fire('성공', '전역 공지가 전송되었습니다.', 'success');
          this.resetGlobalForm();
          this.loadHistory();
        }
      } catch (error) {
        console.error('전역 공지 전송 실패:', error);
        swal.fire('오류', apiErrorMessage(error, '전송에 실패했습니다.'), 'error');
      } finally {
        this.sending = false;
      }
    },

    resetGlobalForm() {
      this.globalForm = { title: '', body: '' };
    },

    async searchClasses() {
      if (!this.classSearch.class_name && !this.classSearch.instructor_name) {
        swal.fire('알림', '클래스명 또는 강사명을 입력해주세요.', 'warning');
        return;
      }
      const search = {};
      if (this.classSearch.class_name) search.class_name = this.classSearch.class_name;
      if (this.classSearch.instructor_name) search.instructor_name = this.classSearch.instructor_name;
      try {
        const response = await this.$axios({
          method: 'get',
          url: '/learning/allClasses',
          params: { search_params: JSON.stringify(search) },
          loadingBar: true,
        });
        let list = [];
        const d = response.data;
        if (Array.isArray(d)) list = d;
        else if (d && Array.isArray(d.results)) list = d.results;
        else if (d && Array.isArray(d.items)) list = d.items;
        else if (d && d.data) {
          if (Array.isArray(d.data)) list = d.data;
          else if (Array.isArray(d.data.items)) list = d.data.items;
        }
        this.classSearch.results = list.map((c) => ({
          ...c,
          id: this.extractClassroomIdx(c),
          name: c.name || c.class_name,
          instructor_name: c.instructor_name || (c.instructor && c.instructor.name) || '-',
        }));
      } catch (error) {
        console.error('클래스 목록 조회 실패:', error);
        swal.fire('오류', apiErrorMessage(error, '클래스 목록을 불러올 수 없습니다.'), 'error');
        this.classSearch.results = [];
      }
    },

    extractClassroomIdx(item) {
      return item?.classroom_idx ?? item?.idx ?? item?.id ?? null;
    },

    isClassroomSelected(item) {
      const id = this.extractClassroomIdx(item);
      return id && this.classForm.classroom_idx === id;
    },

    selectClassroom(item) {
      const id = this.extractClassroomIdx(item);
      if (!id) {
        swal.fire('오류', '클래스 ID를 찾을 수 없습니다.', 'error');
        return;
      }
      this.classForm.classroom_idx = id;
      this.selectedClassroom = item;
    },

    async sendClassNotification() {
      if (!this.classForm.classroom_idx || !this.classForm.title || !this.classForm.body) {
        swal.fire('알림', '모든 필드를 입력해주세요.', 'warning');
        return;
      }
      // 이 API 는 클래스의 승인 수강생 전원에게 발송하고, 서버는 전원 발송을 확인한 요청만 받습니다
      // (confirm_all_class_members 가 true 가 아니면 400). 확인창에서 동의한 경우에만 true 로 보냅니다.
      // 근거: docs/FCM_푸시알림_매뉴얼.md §3.2
      const className = this.selectedClassroom?.name || `클래스 ${this.classForm.classroom_idx}`;
      const confirm = await this.$swal({
        title: '클래스 공지 전송',
        text: `${className}의 수강생 전원에게 발송됩니다. 전송 후에는 취소할 수 없습니다.`,
        icon: 'warning',
        showConfirmButton: true, showCancelButton: true,
        confirmButtonText: '전원에게 전송', cancelButtonText: '취소', reverseButtons: true,
      });
      if (!confirm.isConfirmed) return;

      this.sending = true;
      try {
        const response = await this.$axios({
          method: 'post',
          url: '/notification/send-class-notification/',
          data: {
            title: this.classForm.title,
            body: this.classForm.body,
            classroom_idx: this.classForm.classroom_idx,
            confirm_all_class_members: true,
          },
          loadingBar: true,
        });
        if (response.data.success) {
          swal.fire('성공', this.classResultText(response.data), 'success');
          this.resetClassForm();
          this.loadHistory();
        }
      } catch (error) {
        console.error('클래스 공지 전송 실패:', error);
        swal.fire('전송 실패', apiErrorMessage(error, '전송에 실패했습니다.'), 'error');
      } finally {
        this.sending = false;
      }
    },

    // 발송 결과 인원(docs/FCM_푸시알림_매뉴얼.md §3.2). failure_count 가 0 이어도
    // no_token_count 명은 앱에 로그인한 기기가 없어 받지 못했으므로 따로 알립니다.
    classResultText(result) {
      if (typeof result.target_count !== 'number') return '클래스 공지가 전송되었습니다.';
      let text = `대상 수강생 ${result.target_count}명에게 전송했습니다.`;
      if (result.no_token_count) text += ` 그중 ${result.no_token_count}명은 앱에 로그인한 기기가 없어 받지 못했습니다.`;
      if (result.failure_count) text += ` 기기 ${result.failure_count}대는 발송에 실패했습니다.`;
      return text;
    },

    resetClassForm() {
      this.classForm = { classroom_idx: null, title: '', body: '' };
      this.selectedClassroom = null;
    },

    async searchUsers() {
      if (!this.userSearch.name && !this.userSearch.email) {
        swal.fire('알림', '이름 또는 이메일을 입력해주세요.', 'warning');
        return;
      }
      const search = {};
      if (this.userSearch.name) search.name = this.userSearch.name;
      if (this.userSearch.email) search.email = this.userSearch.email;
      try {
        const response = await this.$axios({
          method: 'get',
          url: '/roles/users',
          params: { search_params: JSON.stringify(search) },
          loadingBar: true,
        });
        let list = [];
        const d = response.data;
        if (Array.isArray(d)) list = d;
        else if (d && Array.isArray(d.results)) list = d.results;
        else if (d && Array.isArray(d.items)) list = d.items;
        else if (d && d.data) {
          if (Array.isArray(d.data)) list = d.data;
          else if (Array.isArray(d.data.items)) list = d.data.items;
        }
        this.userSearch.results = list.map((u) => ({ ...u, id: this.extractUserIdx(u) }));
      } catch (error) {
        console.error('사용자 목록 조회 실패:', error);
        swal.fire('오류', apiErrorMessage(error, '사용자 목록을 불러올 수 없습니다.'), 'error');
        this.userSearch.results = [];
      }
    },

    extractUserIdx(item) {
      return item?.usersIdx ?? item?.users_idx ?? item?.idx ?? item?.id ?? null;
    },

    isUserSelected(item) {
      const id = this.extractUserIdx(item);
      return id && this.personalForm.usersIdx === id;
    },

    selectUser(item) {
      const id = this.extractUserIdx(item);
      if (!id) {
        swal.fire('오류', '사용자 ID를 찾을 수 없습니다.', 'error');
        return;
      }
      this.personalForm.usersIdx = id;
      this.selectedUser = item;
    },

    validatePersonalData() {
      if (!this.personalForm.dataJson) {
        this.personalDataError = '';
        return true;
      }
      try {
        JSON.parse(this.personalForm.dataJson);
        this.personalDataError = '';
        return true;
      } catch (error) {
        this.personalDataError = '유효하지 않은 JSON 형식입니다.';
        return false;
      }
    },

    async sendPersonalNotification() {
      if (!this.personalForm.usersIdx || !this.personalForm.title || !this.personalForm.body) {
        swal.fire('알림', '필수 필드를 모두 입력해주세요.', 'warning');
        return;
      }
      if (!this.validatePersonalData()) return;

      const confirm = await this.$swal({
        title: '개인 알림 전송',
        text: `사용자 ${this.personalForm.usersIdx}에게 알림을 전송하시겠습니까?`,
        icon: 'question',
        showConfirmButton: true, showCancelButton: true,
        confirmButtonText: '전송', cancelButtonText: '취소', reverseButtons: true,
      });
      if (!confirm.isConfirmed) return;

      this.sending = true;
      try {
        const data = {
          usersIdx: this.personalForm.usersIdx,
          title: this.personalForm.title,
          body: this.personalForm.body,
        };
        if (this.personalForm.dataJson) data.data = JSON.parse(this.personalForm.dataJson);

        const response = await this.$axios({
          method: 'post',
          url: '/notification/send-personal-notification/',
          data,
          loadingBar: true,
        });
        if (response.data.success) {
          swal.fire('성공', '개인 알림이 전송되었습니다.', 'success');
          this.resetPersonalForm();
          this.loadHistory();
        }
      } catch (error) {
        console.error('개인 알림 전송 실패:', error);
        swal.fire('오류', apiErrorMessage(error, '전송에 실패했습니다.'), 'error');
      } finally {
        this.sending = false;
      }
    },

    resetPersonalForm() {
      this.personalForm = { usersIdx: null, title: '', body: '', dataJson: '' };
      this.selectedUser = null;
      this.personalDataError = '';
    },

    async loadHistory(page = 1) {
      this.historyPage = page;
      this.historyLoading = true;
      try {
        const params = {};
        if (this.historyFilter.notification_type) params.notification_type = this.historyFilter.notification_type;
        if (this.historyFilter.success !== '') params.success = this.historyFilter.success;
        if (this.historyFilter.sent_from) params.sent_from = new Date(this.historyFilter.sent_from).toISOString();
        if (this.historyFilter.sent_to) params.sent_to = new Date(this.historyFilter.sent_to).toISOString();

        const response = await this.$axios({
          method: 'get',
          url: '/notification/admin/notification-history/',
          params,
          loadingBar: true,
        });

        if (response.data && response.data.success) {
          this.historyList = response.data.data || [];
        } else if (Array.isArray(response.data)) {
          this.historyList = response.data;
        } else if (response.data && response.data.data) {
          this.historyList = response.data.data;
        } else {
          this.historyList = [];
        }
      } catch (error) {
        console.error('이력 조회 실패:', error);
        if (error.response?.status === 404 || error.response?.data?.detail?.includes('not found')) {
          this.historyList = [];
        } else {
          swal.fire('오류', apiErrorMessage(error, '이력 조회에 실패했습니다.'), 'error');
          this.historyList = [];
        }
      } finally {
        this.historyLoading = false;
      }
    },

    resetHistoryFilter() {
      this.historyFilter = { notification_type: '', success: '', sent_from: '', sent_to: '' };
      this.loadHistory(1);
    },

    showHistoryDetail(item) {
      this.selectedHistory = item;
      this.historyDetailOpen = true;
    },

    async getDuplicateStats() {
      this.adminLoading = true;
      try {
        const response = await this.$axios({
          method: 'get',
          url: '/notification/admin/duplicate-stats/',
          loadingBar: true,
        });
        if (response.data.success) {
          this.duplicateStats = response.data.data;
          swal.fire('조회 완료', '중복 통계를 조회했습니다.', 'success');
        }
      } catch (error) {
        console.error('중복 통계 조회 실패:', error);
        swal.fire('오류', '통계 조회에 실패했습니다.', 'error');
      } finally {
        this.adminLoading = false;
      }
    },

    async cleanupDuplicates() {
      const confirm = await this.$swal({
        title: '중복 토큰 정리',
        text: '중복된 토큰을 정리하시겠습니까? 이 작업은 되돌릴 수 없습니다.',
        icon: 'warning',
        showConfirmButton: true, showCancelButton: true,
        confirmButtonText: '정리', cancelButtonText: '취소', reverseButtons: true,
      });
      if (!confirm.isConfirmed) return;

      this.adminLoading = true;
      try {
        const response = await this.$axios({
          method: 'post',
          url: '/notification/admin/cleanup-duplicates/',
          loadingBar: true,
        });
        if (response.data.success) {
          swal.fire('성공', '중복 토큰이 정리되었습니다.', 'success');
          this.getDuplicateStats();
        }
      } catch (error) {
        console.error('중복 토큰 정리 실패:', error);
        swal.fire('오류', '토큰 정리에 실패했습니다.', 'error');
      } finally {
        this.adminLoading = false;
      }
    },

    formatDateTime(datetime) {
      if (!datetime) return '-';
      const date = new Date(datetime);
      return date.toLocaleString('ko-KR', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit',
      });
    },

    getTypeLabel(type) {
      const labels = { global: '전역', class: '클래스', personal: '개인' };
      return labels[type] || type;
    },

    typePillKind(type) {
      const map = { global: 'info', class: 'ok', personal: 'warn' };
      return map[type] || 'default';
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.push-msg {
  display: flex;
  flex-direction: column;
  gap: 18px;

  &__pane {
    padding: 20px;
  }

  &__grid {
    display: grid;
    grid-template-columns: 1fr 320px;
    gap: 18px;
    align-items: start;

    @media (max-width: 992px) {
      grid-template-columns: 1fr;
    }
  }

  &__main {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__side {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  &__side-h {
    margin: 0 0 12px;
    font-size: 12px;
    font-weight: 800;
    color: $p-ink;
  }

  &__sub-h {
    margin: 0 0 4px;
    font-size: 14px;
    font-weight: 800;
    color: $p-ink;
    display: inline-flex;
    align-items: center;
    gap: 6px;

    > i { font-size: 18px; color: $p-point-deep; }
  }

  &__sub-d {
    margin: 0 0 8px;
    font-size: 11px;
    color: $p-ink-soft;
  }

  &__guide {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 10px;

    li {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 12px;
      color: $p-ink-mid;
    }
  }

  &__search-block {
    background: $p-panel-alt;
    padding: 12px 14px;
    border-radius: $p-radius-md;
    border: 1px solid $p-line;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__search-action {
    display: flex;
    align-items: end;
  }

  &__no-result {
    margin: 8px 0 0;
    font-size: 11px;
    color: $p-ink-soft;
  }

  &__alert {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 14px;
    background: $p-info-soft;
    border-left: 3px solid $p-info;
    border-radius: $p-radius-md;
    color: $p-ink2;
    font-size: 12px;

    > i { font-size: 18px; color: $p-info; flex-shrink: 0; }

    ul { margin: 4px 0 0 16px; padding: 0; font-size: 11px; }

    &--info {
      background: $p-info-soft;
      border-color: $p-info;
      > i { color: $p-info; }
    }
    &--ok {
      background: $p-ok-soft;
      border-color: $p-ok;
      > i { color: $p-ok; }
    }
    &--warn {
      background: $p-warn-soft;
      border-color: $p-warn;
      color: $p-warn;
      > i { color: $p-warn; }
    }
    &--err {
      background: $p-err-soft;
      border-color: $p-err;
      color: $p-err;
      > i { color: $p-err; }
    }
  }

  &__actions {
    display: flex;
    gap: 8px;
    margin-top: 6px;

    > .a-btn:first-child { flex: 1; }
  }

  &__json {
    font-family: $p-font-mono;
    font-size: 11px;
  }

  &__code {
    margin: 0;
    padding: 10px 12px;
    background: $p-ink;
    color: $p-point;
    border-radius: $p-radius-md;
    font-family: $p-font-mono;
    font-size: 11px;
    line-height: 1.5;
    overflow-x: auto;
  }

  &__history-stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    margin-bottom: 18px;

    @media (max-width: 768px) { grid-template-columns: repeat(2, 1fr); }
  }

  &__pager-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 16px;
    border-top: 1px solid $p-line;
    flex-wrap: wrap;
  }

  &__pager-total {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    font-size: 12px;
    color: $p-ink-mid;
    font-weight: 600;

    > strong {
      color: $p-ink;
      font-family: $p-font-mono;
      font-weight: 800;
    }
  }

  &__pager-size {
    width: 100px;
    height: 30px;
    padding: 0 8px;
    font-size: 12px;
  }

  &__pager {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__pager-num {
    min-width: 32px;
    height: 32px;
    padding: 0 8px;
    border-radius: $p-radius-sm;
    border: 1px solid $p-line;
    background: $p-panel;
    color: $p-ink-mid;
    font-family: $p-font-mono;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    transition: background $p-dur $p-ease, color $p-dur $p-ease;

    &:hover { background: $p-point-soft; color: $p-point-deep; }

    &.is-active {
      background: $p-point-deep;
      border-color: $p-point-deep;
      color: $p-panel;
    }
  }

  &__filter-card {
    background: $p-panel-alt;
    padding: 14px 16px;
    border-radius: $p-radius-md;
    border: 1px solid $p-line;
    margin-bottom: 18px;
  }

  &__filter-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 12px;
  }

  &__filters {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 10px;
  }

  &__filter {
    flex: 0 0 auto;

    &--sm { width: 130px; }
    &--md { width: 200px; }
  }

  &__filter-btns {
    display: flex;
    align-items: flex-end;
    gap: 6px;
    padding-bottom: 1px;
  }

  &__num {
    font-family: $p-font-mono;
    font-variant-numeric: tabular-nums;
    font-size: 11px;
  }

  &__admin-d {
    font-size: 12px;
    color: $p-ink-mid;
    margin: 8px 0 14px;
  }

  &__admin-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin-top: 12px;

    @media (max-width: 576px) { grid-template-columns: 1fr; }
  }

  &__guide-card {
    margin-top: 18px;
  }

  &__guide-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;
    margin-top: 14px;

    @media (max-width: 768px) { grid-template-columns: repeat(2, 1fr); }
  }

  &__guide-item {
    text-align: center;

    h6 { margin: 8px 0 4px; font-size: 12px; font-weight: 800; color: $p-ink; }
    p { margin: 0; font-size: 11px; color: $p-ink-soft; }
  }

  &__guide-icon {
    width: 48px;
    height: 48px;
    margin: 0 auto;
    border-radius: $p-radius-md;
    background: $p-point-soft;
    color: $p-point-deep;
    display: grid;
    place-items: center;

    > i { font-size: 22px; }

    &--ok   { background: $p-ok-soft;   color: $p-ok; }
    &--warn { background: $p-warn-soft; color: $p-warn; }
    &--info { background: $p-info-soft; color: $p-info; }
  }

  // 모달 상세
  &__detail {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__detail-row {
    display: grid;
    grid-template-columns: 100px 1fr;
    gap: 12px;
    align-items: center;
    padding-bottom: 10px;
    border-bottom: 1px solid $p-line-soft;

    &:last-child { border-bottom: 0; }
  }

  &__detail-label {
    font-size: 11px;
    font-weight: 700;
    color: $p-ink-soft;
  }

  &__detail-val {
    font-size: 13px;
    color: $p-ink;
    word-break: break-word;
  }
}

.p-tabs {
  display: flex;
  border-bottom: 1px solid $p-line;
  padding: 0 12px;
  overflow-x: auto;

  &__item {
    border: 0;
    background: transparent;
    color: $p-ink-mid;
    font-family: $p-font-body;
    font-size: 12px;
    font-weight: 700;
    padding: 12px 18px;
    border-bottom: 3px solid transparent;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: color $p-dur $p-ease, border-color $p-dur $p-ease;
    margin-bottom: -1px;

    > i { font-size: 16px; }

    &:hover { color: $p-ink; }

    &.is-active {
      color: $p-point-deep;
      border-bottom-color: $p-point-deep;
    }
  }
}
</style>
